import clsx from 'clsx'
import { useMemo, type ReactNode } from 'react'
import { View } from 'react-native'

import { Icon } from '@/src/elements/media/Icon'
import { Typography } from '@/src/elements/text/Typography'
import { ICON } from '@/src/general/enums/icons'
import { type TMessageKey, useTranslate } from '@/src/lang/useTranslate'

import { PUBLICATION_CONTENT_TYPE } from '../../../models/publication.constants'

const getContentType = (content: string) => {
  if (
    content.includes('rcf.fr') ||
    content.includes('ausha.co') ||
    content.includes('.fm') ||
    content.includes('soundcloud') ||
    content.includes('.mp3')
  ) {
    return PUBLICATION_CONTENT_TYPE.AUDIO
  }
  if (content.includes('youtu')) {
    return PUBLICATION_CONTENT_TYPE.VIDEO
  }
  return PUBLICATION_CONTENT_TYPE.READING
}

const contentTypesKeysMap: Record<PUBLICATION_CONTENT_TYPE, TMessageKey> = {
  [PUBLICATION_CONTENT_TYPE.AUDIO]: 'user.wizard.audio',
  [PUBLICATION_CONTENT_TYPE.VIDEO]: 'user.wizard.video',
  [PUBLICATION_CONTENT_TYPE.READING]: 'user.wizard.reading',
}

export const PublicationContentLabel = ({
  content,
  colors,
  className,
}: {
  content: string
  colors: 'success' | 'default' | 'primary'
  className?: string
}): ReactNode => {
  const { t } = useTranslate()

  const contentDuration = 0 // TODO : Get content duration
  const contentType = getContentType(content)

  const colorsClassName = {
    success: 'text-success-text-contrast-low native:text-success-text-contrast-low',
    default: 'text-default-icon-contrast-lower native:text-default-icon-contrast-lower',
    primary: 'text-primary-text-contrast-low native:text-primary-text-contrast-low',
  }
  const iconName = useMemo(() => {
    if (contentType === PUBLICATION_CONTENT_TYPE.AUDIO) return ICON.AUDIO
    if (contentType === PUBLICATION_CONTENT_TYPE.VIDEO) return ICON.YOUTUBE
    return ICON.BOOK_OPEN
  }, [contentType])

  return (
    <View className="flex-row-reverse gap-4 text-right">
      <View className={clsx('flex flex-row items-center gap-1', colorsClassName[colors], className)}>
        <Icon size="xs" name={iconName} className={clsx(colorsClassName[colors], className)} />
        <Typography
          colors={colors || 'default'}
          contrast="low"
          size="xs"
          className={clsx(colorsClassName[colors], className)}
        >
          {t(contentTypesKeysMap[contentType])}
        </Typography>
      </View>
      {!!contentDuration && (
        <View className={clsx('flex flex-row items-center gap-1', colorsClassName[colors], className)}>
          <Icon size="xs" name={ICON.HOURGLASS_HALF} className={clsx(colorsClassName[colors], className)} />
          <Typography
            colors={colors || 'default'}
            contrast="low"
            size="xs"
            className={clsx(colorsClassName[colors], className)}
          >
            {contentDuration + ' ' + t('user.wizard.minutes')}
          </Typography>
        </View>
      )}
    </View>
  )
}
